import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const TOKEN_KEY = "aran_admin_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

export const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const path = error.config?.url || "";
    const adminRequest = path.startsWith("/admin/") || path === "/admin" || path === "/auth/me";
    if ((status === 401 || status === 403) && adminRequest && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("aran:admin-session-invalid", { detail: { status } }));
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "حدث خطأ ما. حاول مرة أخرى.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// ---- Public ----
export const fetchBootstrap = () => api.get("/bootstrap").then((r) => r.data);
export const fetchPage = (slug) => api.get(`/page/${slug}`).then((r) => r.data);
export const analyzeUrl = (platform_key, url) =>
  api.post("/analyze", { platform_key, url }).then((r) => r.data);
export const downloadUrl = (token, q) =>
  `${API_BASE}/download?token=${encodeURIComponent(token)}&q=${encodeURIComponent(q)}`;
export const track = (payload) =>
  api.post("/track", payload).catch(() => {});

// ---- Auth ----
export const adminLogin = (email, password) =>
  api.post("/auth/login", { email, password }).then((r) => r.data);
export const adminMe = () => api.get("/auth/me").then((r) => r.data);
export const adminLogout = () => api.post("/auth/logout").catch(() => {});

// ---- Admin ----
export const adminDashboard = () => api.get("/admin/dashboard").then((r) => r.data);
export const adminListPlatforms = () => api.get("/admin/platforms").then((r) => r.data);
export const adminCreatePlatform = (data) => api.post("/admin/platforms", data).then((r) => r.data);
export const adminUpdatePlatform = (id, data) => api.put(`/admin/platforms/${id}`, data).then((r) => r.data);
export const adminDeletePlatform = (id) => api.delete(`/admin/platforms/${id}`).then((r) => r.data);
export const adminReorderPlatforms = (ids) => api.post("/admin/platforms/reorder", { ordered_ids: ids }).then((r) => r.data);
export const adminListTools = () => api.get("/admin/tools").then((r) => r.data);
export const adminCreateTool = (data) => api.post("/admin/tools", data).then((r) => r.data);
export const adminUpdateTool = (id, data) => api.put(`/admin/tools/${id}`, data).then((r) => r.data);
export const adminDeleteTool = (id) => api.delete(`/admin/tools/${id}`).then((r) => r.data);
export const adminGetToolSeo = (id) => api.get(`/admin/tools/${id}/seo`).then((r) => r.data);
export const adminCreateToolSeo = (id, data) => api.post(`/admin/tools/${id}/seo`, data).then((r) => r.data);
export const adminUpdateToolSeo = (id, contentId, data) =>
  api.put(`/admin/tools/${id}/seo/${contentId}`, data).then((r) => r.data);
export const adminDeleteToolSeo = (id, contentId) =>
  api.delete(`/admin/tools/${id}/seo/${contentId}`).then((r) => r.data);
export const fetchPublishedToolSeo = (slug, language = "ar") =>
  api.get(`/tools/${encodeURIComponent(slug)}/seo`, { params: { lang: language } }).then((r) => r.data);
export const adminListPages = () => api.get("/admin/pages").then((r) => r.data);
export const adminUpdatePage = (slug, data) => api.put(`/admin/pages/${slug}`, data).then((r) => r.data);
export const adminListAdSlots = () => api.get("/admin/ad-slots").then((r) => r.data);
export const adminUpdateAdSlot = (key, data) => api.put(`/admin/ad-slots/${key}`, data).then((r) => r.data);
export const adminGetSettings = () => api.get("/admin/settings").then((r) => r.data);
export const adminUpdateSettings = (values) => api.put("/admin/settings", { values }).then((r) => r.data);
export const adminListLogs = (params = {}) =>
  api.get("/admin/logs", { params }).then((r) => r.data);

// ---- Educational content ----
export const fetchArticles = (params = {}) => api.get("/articles", { params }).then((r) => r.data);
export const fetchArticle = (slug, language = "ar") =>
  api.get(`/articles/${encodeURIComponent(slug)}`, { params: { language } }).then((r) => r.data);
export const fetchArticleCategories = (language = "ar") =>
  api.get("/article-categories", { params: { language } }).then((r) => r.data);
export const fetchPublishedHomeContent = () => api.get("/home-content").then((r) => r.data);
export const adminListArticles = (params = {}) => api.get("/admin/articles", { params }).then((r) => r.data);
export const adminGetArticle = (id) => api.get(`/admin/articles/${id}`).then((r) => r.data);
export const adminCreateArticle = (data) => api.post("/admin/articles", data).then((r) => r.data);
export const adminUpdateArticle = (id, data) => api.put(`/admin/articles/${id}`, data).then((r) => r.data);
export const adminPublishArticle = (id) => api.post(`/admin/articles/${id}/publish`).then((r) => r.data);
export const adminDraftArticle = (id) => api.post(`/admin/articles/${id}/draft`).then((r) => r.data);
export const adminDeleteArticle = (id) => api.delete(`/admin/articles/${id}`).then((r) => r.data);
export const adminListArticleCategories = (language) =>
  api.get("/admin/article-categories", { params: language ? { language } : {} }).then((r) => r.data);
export const adminCreateArticleCategory = (data) => api.post("/admin/article-categories", data).then((r) => r.data);
export const adminUpdateArticleCategory = (id, data) => api.put(`/admin/article-categories/${id}`, data).then((r) => r.data);
export const adminDeleteArticleCategory = (id) => api.delete(`/admin/article-categories/${id}`).then((r) => r.data);
export const adminGetHomeContent = () => api.get("/admin/home-content").then((r) => r.data);
export const adminSaveHomeContentDraft = (data) => api.put("/admin/home-content/draft", data).then((r) => r.data);
export const adminPublishHomeContent = () => api.post("/admin/home-content/publish").then((r) => r.data);
